import React, { useState, useEffect, useMemo, useRef } from 'react';
import PendingApprovalBanner from '../components/workorder/PendingApprovalBanner';
import { useSearchParams } from 'react-router-dom';
import { jobsAPI, clientsAPI } from '../lib/api';
import { useCap } from '../components/auth/RequireCap';
import { useEffectivePermissions } from '../hooks/useEffectivePermissions';
import JobEditor from '../components/job/JobEditor';
import { toast } from 'sonner';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import { Label } from '../components/ui/label';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Plus, Search, Briefcase, Pencil, Archive, ArchiveRestore, Lock } from 'lucide-react';

export default function Jobs() {
  const enginePerms = useEffectivePermissions();
  const canView = useCap('jobs', 'view');
  const canAdd = useCap('jobs', 'add');
  const canEdit = useCap('jobs', 'edit');
  const [searchParams, setSearchParams] = useSearchParams();
  const clientParam = searchParams.get('client') || '';

  const [jobs, setJobs] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('active');
  const [clientFilter, setClientFilter] = useState(clientParam || 'all');
  const [editorOpen, setEditorOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editingClientId, setEditingClientId] = useState('');

  useEffect(() => { loadAll(); }, []);

  const jobParam = searchParams.get('job');
  const accessChecked = useRef(null);

  const setJobParam = (jobNumber) => {
    const p = {};
    if (clientParam) p.client = clientParam;
    if (jobNumber) p.job = String(jobNumber);
    setSearchParams(p);  // push (not replace) so browser Back returns to the list
  };

  // URL <-> editor sync. The job pop-up is driven by ?job=<number> so the link is
  // shareable/bookmarkable and browser Back closes it. Paste while signed in opens
  // the list with that job's pop-up already open.
  useEffect(() => {
    if (loading) return;
    if (!jobParam) {
      // URL has no job: close any deep-linked (edit-mode) pop-up. Leave create alone.
      if (editorOpen && editingId) setEditorOpen(false);
      accessChecked.current = null;
      return;
    }
    const target = jobs.find((j) => String(j.job_number) === String(jobParam));
    if (target) {
      if (!editorOpen || editingId !== target.id) {
        setEditingId(target.id);
        setEditingClientId(target.client_id);
        setEditorOpen(true);
      }
      return;
    }
    // Not in the accessible list — distinguish "no access" from "not found" once.
    if (accessChecked.current === jobParam) return;
    accessChecked.current = jobParam;
    (async () => {
      try {
        const { data } = await jobsAPI.resolveByNumber(jobParam);
        if (data?.accessible && data?.id) {
          await loadAll();  // it exists & is accessible but wasn't loaded — refresh
        } else if (data?.exists) {
          toast.error("You don't have access to this record");
          setJobParam(null);
        } else {
          toast.error(`Job ${jobParam} was not found`);
          setJobParam(null);
        }
      } catch {
        toast.error(`Job ${jobParam} was not found`);
        setJobParam(null);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jobParam, jobs, loading]);

  const loadAll = async () => {
    setLoading(true);
    try {
      const [jobsRes, clientsRes] = await Promise.all([
        jobsAPI.list(),
        clientsAPI.list(),
      ]);
      setJobs(jobsRes.data);
      setClients(clientsRes.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load jobs');
    } finally {
      setLoading(false);
    }
  };

  const clientById = useMemo(() => {
    const m = new Map();
    clients.forEach(c => m.set(c.id, c));
    return m;
  }, [clients]);

  const filtered = useMemo(() => {
    const q = search.toLowerCase().trim();
    return jobs
      .filter(j => {
        if (statusFilter === 'active' && !j.is_active) return false;
        if (statusFilter === 'inactive' && j.is_active) return false;
        if (clientFilter !== 'all' && j.client_id !== clientFilter) return false;
        if (!q) return true;
        const c = clientById.get(j.client_id);
        return (
          j.job_number?.toLowerCase().includes(q) ||
          j.job_description?.toLowerCase().includes(q) ||
          j.site_city?.toLowerCase().includes(q) ||
          j.po_number?.toLowerCase().includes(q) ||
          c?.name?.toLowerCase().includes(q)
        );
      });
  }, [jobs, search, statusFilter, clientFilter, clientById]);

  const openCreate = () => {
    setEditingId(null);
    setEditingClientId(clientFilter !== 'all' ? clientFilter : '');
    setEditorOpen(true);
  };
  const openEdit = (job) => {
    setEditingId(job.id);
    setEditingClientId(job.client_id);
    setEditorOpen(true);
    if (String(job.job_number) !== String(jobParam)) setJobParam(job.job_number);
  };

  const onEditorOpenChange = (o) => {
    setEditorOpen(o);
    if (!o && jobParam) setJobParam(null);  // closing a linked job returns to /jobs
  };

  const toggleActive = async (job) => {
    try {
      await jobsAPI.setActive(job.id, !job.is_active);
      toast.success(job.is_active ? 'Job deactivated' : 'Job reactivated');
      await loadAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to update status');
    }
  };

  if (enginePerms?.loaded && !canView) {
    return (
      <div className="flex items-center justify-center h-96">
        <Card className="max-w-md">
          <CardContent className="p-6 text-center">
            <Lock className="w-8 h-8 mx-auto text-slate-400 mb-2" />
            <h2 className="text-lg font-semibold text-slate-700">Access denied</h2>
            <p className="text-sm text-slate-500 mt-1">You don&apos;t have permission to view Jobs.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="jobs-page">
      <PendingApprovalBanner kind="jobs" />
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-semibold flex items-center gap-2">
            <Briefcase className="w-6 h-6 text-slate-500" />
            Jobs
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            Client jobs are the billing unit. Each job holds rate lines that consume the Billing Item catalog.
          </p>
        </div>
        {canAdd && (
          <Button onClick={openCreate} data-testid="jobs-create-btn">
            <Plus className="w-4 h-4 mr-1.5" /> New Job
          </Button>
        )}
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-4 flex flex-wrap items-end gap-4">
          <div className="flex-1 min-w-[240px]">
            <Label className="text-xs">Search</Label>
            <div className="relative">
              <Search className="w-4 h-4 absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
              <Input
                value={search} onChange={(e) => setSearch(e.target.value)}
                placeholder="Job number, description, city, PO#, client…"
                className="pl-8" data-testid="jobs-search"
              />
            </div>
          </div>
          <div>
            <Label className="text-xs">Client</Label>
            <Select
              value={clientFilter}
              onValueChange={(v) => {
                setClientFilter(v);
                setSearchParams(v === 'all' ? {} : { client: v });
              }}
            >
              <SelectTrigger className="w-[260px]" data-testid="jobs-client-filter"><SelectValue /></SelectTrigger>
              <SelectContent className="max-h-72">
                <SelectItem value="all">All clients</SelectItem>
                {clients.map(c => (
                  <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">Status</Label>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-[140px]" data-testid="jobs-status-filter"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="active">Active</SelectItem>
                <SelectItem value="inactive">Inactive</SelectItem>
                <SelectItem value="all">All</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Table */}
      <Card>
        <CardContent className="p-0">
          {loading ? (
            <p className="p-6 text-sm text-slate-400">Loading jobs…</p>
          ) : filtered.length === 0 ? (
            <div className="p-10 text-center">
              <Briefcase className="w-8 h-8 mx-auto text-slate-300 mb-2" />
              <p className="text-slate-500">
                {jobs.length === 0 ? 'No jobs yet. Create your first job or import from WinTeam.' : 'No jobs match your filters.'}
              </p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Job Number</TableHead>
                  <TableHead>Client</TableHead>
                  <TableHead>Description</TableHead>
                  <TableHead>Site</TableHead>
                  <TableHead>Company</TableHead>
                  <TableHead>Billing</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right w-40">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filtered.map(job => {
                  const c = clientById.get(job.client_id);
                  const site = [job.site_city, job.site_state].filter(Boolean).join(', ');
                  return (
                    <TableRow key={job.id} data-testid={`job-row-${job.id}`} className={job.is_active ? '' : 'text-slate-400'}>
                      <TableCell className="font-mono text-xs">{job.job_number}</TableCell>
                      <TableCell className="font-medium">{c?.name || '—'}</TableCell>
                      <TableCell className="max-w-xs truncate">{job.job_description || '—'}</TableCell>
                      <TableCell className="text-sm">{site || '—'}</TableCell>
                      <TableCell className="text-xs">{job.company || '—'}</TableCell>
                      <TableCell className="text-xs">{job.billing_frequency || '—'}</TableCell>
                      <TableCell>
                        {job.is_active
                          ? <Badge className="bg-emerald-100 text-emerald-800 hover:bg-emerald-100">Active</Badge>
                          : <Badge variant="secondary">Inactive</Badge>}
                      </TableCell>
                      <TableCell className="text-right">
                        <Button size="icon" variant="ghost" onClick={() => openEdit(job)} data-testid={`edit-job-${job.id}`}>
                          <Pencil className="w-4 h-4" />
                        </Button>
                        {canEdit && (
                          <Button size="icon" variant="ghost" onClick={() => toggleActive(job)} data-testid={`toggle-job-${job.id}`}>
                            {job.is_active
                              ? <Archive className="w-4 h-4 text-slate-500" />
                              : <ArchiveRestore className="w-4 h-4 text-emerald-600" />}
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <JobEditor
        open={editorOpen}
        onOpenChange={onEditorOpenChange}
        jobId={editingId}
        clientId={editingClientId}
        canEdit={canEdit}
        onSaved={loadAll}
      />
    </div>
  );
}
